import type { ReactElement } from 'react'
import { cn } from 'app/lib/utils'

/**
 * A control label that shortens as its toolbar narrows, so the row stays on one line.
 *
 * Three tiers, measured against the nearest `@container/toolbar` (the left half of a list
 * or table toolbar) rather than the viewport, because the sidebar and the toolbar's right
 * half decide how much room the controls really have:
 *
 * - wide (768px and up): the full label
 * - middle (576px to 768px): `short` when given, otherwise the full label
 * - narrow (under 576px): nothing when `iconOnly`, since the control's icon carries it;
 *   otherwise `short` or the full label
 *
 * Outside any toolbar container none of the max-width rules match, so the full label shows,
 * which is what the toggle-off pages and the focus bar want.
 */
export function CompactLabel({
  full,
  short,
  iconOnly = false,
  className,
}: {
  full: string
  short?: string
  /** The control has an icon that can stand for it, so the label may vanish when narrow. */
  iconOnly?: boolean
  className?: string
}): ReactElement {
  const hideFullBelow = short ? `@max-3xl/toolbar:hidden` : iconOnly ? `@max-xl/toolbar:hidden` : undefined
  return (
    <>
      <span className={cn(className, hideFullBelow)}>{full}</span>
      {short && (
        <span className={cn(className, `hidden @max-3xl/toolbar:inline`, iconOnly && `@max-xl/toolbar:hidden`)}>
          {short}
        </span>
      )}
    </>
  )
}

/** The class a toolbar's left half carries so CompactLabel can measure it. */
export const TOOLBAR_CONTAINER = `@container/toolbar`
